import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FileText, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Mail, 
  Clock,
  User,
  Calendar,
  Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../../utils/api';
import './WebFormLogs.css';

const WebFormLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, success, error
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/webform/api-keys/logs');
      setLogs(response || []);
    } catch (error) {
      console.error('Failed to load webform logs:', error);
      toast.error('Failed to load webform logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    // Filter by status
    if (filter !== 'all' && log.status !== filter) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const formDataStr = JSON.stringify(log.formData).toLowerCase();
      return formDataStr.includes(search) || 
             log.error?.toLowerCase().includes(search) ||
             log.noteId?.toString().includes(search);
    }
    
    return true;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (loading) {
    return (
      <div className="webform-logs-container">
        <div className="loading-state">
          <RefreshCw className="spinner" />
          <p>Loading webform logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="webform-logs-container">
      <div className="logs-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({logs.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'success' ? 'active' : ''}`}
              onClick={() => setFilter('success')}
            >
              <CheckCircle size={16} />
              Success ({logs.filter(l => l.status === 'success').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'error' ? 'active' : ''}`}
              onClick={() => setFilter('error')}
            >
              <XCircle size={16} />
              Errors ({logs.filter(l => l.status === 'error').length})
            </button>
          </div>
        </div>
        
        <div className="search-group">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <button className="btn btn-secondary refresh-btn" onClick={loadLogs}>
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      <div className="logs-content">
        {filteredLogs.length === 0 ? (
          <div className="no-logs">
            <FileText size={48} />
            <p>No webform submissions found</p>
            {searchTerm && <small>Try adjusting your search or filter</small>}
          </div>
        ) : (
          <div className="logs-list">
            {filteredLogs.map((log) => (
              <div key={log.logId} className={`log-entry ${log.status}`}>
                <div className="log-header">
                  <div className="log-status">
                    {log.status === 'success' ? (
                      <CheckCircle size={20} className="success-icon" />
                    ) : (
                      <XCircle size={20} className="error-icon" />
                    )}
                    <span className="status-text">{log.status.toUpperCase()}</span>
                  </div>
                  <div className="log-meta">
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span>{formatDate(log.timestamp)}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={14} />
                      <span>{formatDuration(log.duration)}</span>
                    </div>
                  </div>
                </div>

                <div className="log-details">
                  {log.status === 'success' ? (
                    <>
                      <div className="detail-row">
                        <strong>Note ID:</strong>
                        <span>{log.noteId}</span>
                      </div>
                      <div className="detail-row">
                        <strong>Template:</strong>
                        <span>{log.template}</span>
                      </div>
                      <div className="detail-row">
                        <strong>User ID:</strong>
                        <span>{log.userId}</span>
                      </div>
                      <div className="detail-row">
                        <strong>Email Sent:</strong>
                        <span className={log.emailSent ? 'success' : 'warning'}>
                          {log.emailSent ? '✓ Yes' : '✗ No'}
                        </span>
                      </div>
                      {log.emailRecipient && (
                        <div className="detail-row">
                          <strong>Email Recipient:</strong>
                          <span>{log.emailRecipient}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="error-message">
                      <strong>Error:</strong>
                      <span>{log.error}</span>
                    </div>
                  )}

                  <div className="detail-row">
                    <strong>IP Address:</strong>
                    <span>{log.ipAddress}</span>
                  </div>

                  {log.formData && (
                    <details className="form-data-section">
                      <summary>Form Data</summary>
                      <div className="form-data-content">
                        {Object.entries(log.formData)
                          .filter(([key]) => !key.startsWith('_'))
                          .map(([key, value]) => (
                            <div key={key} className="form-field">
                              <strong>{key}:</strong>
                              <span>{value}</span>
                            </div>
                          ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebFormLogs;
